<script lang="ts" context="module">
	/**
	 * Radio-group names for the pickers that are given no id.
	 *
	 * A radio group is a group because its inputs share a `name`, and that is also what makes the
	 * arrow keys walk it. Two pickers on one screen — the analytics filter beside a form, the
	 * add-currency dialog over the price list — sharing a name would be **one** group: choosing in
	 * the second would clear the first, which reads as the page forgetting what was picked.
	 */
	let sequence = 0;

	/**
	 * How far to nudge a symbol so that the **ink** sits in the middle of its section.
	 *
	 * Text is laid out on a baseline, which is right everywhere else and wrong here: a section holds
	 * one glyph and nothing else, so there is no line of text for the baseline to belong to and the
	 * eye judges the glyph against the pill around it. `$` inks from above cap height to below the
	 * baseline; `€` stops at the baseline and starts lower. Sharing a baseline therefore puts them
	 * at visibly different heights — measured on the real thing, `€` sat about 1.5px low beside `$`
	 * — and every symbol an admin can type (`¥`, `£`, `kr`, `R$`, `zł`) is its own version of that.
	 *
	 * So the glyph is measured and shifted: the box's centre relative to the baseline is
	 * `(fontDescent − fontAscent) / 2`, the ink's is `(inkDescent − inkAscent) / 2`, and the
	 * difference is the nudge. It is a `transform`, so it moves the glyph and not the layout, and it
	 * is **rounded to a whole pixel** because a fractional offset rasterises text blurry for the sake
	 * of a correction nobody can see.
	 *
	 * Returns 0 wherever it cannot measure — no canvas under jsdom, and none in a browser that has
	 * refused one. A glyph left on its baseline is what it would have had
	 * anyway, so the failure mode is the old rendering rather than a broken one.
	 */
	const nudges = new Map<string, number>();

	/*
	 * Nothing is cached until the faces have settled. A webfont arriving late changes the metrics
	 * without changing the font *string* the cache is keyed on, so an early answer would be kept
	 * for a face that is no longer the one on screen.
	 */
	function fontsSettled(): boolean {
		return typeof document === 'undefined' || document.fonts === undefined || document.fonts.status === 'loaded';
	}

	export function opticalNudge(symbol: string, font: string): number {
		const key = `${font}|${symbol}`;
		const settled = fontsSettled();
		const cached = settled ? nudges.get(key) : undefined;
		if (cached !== undefined) return cached;

		let shift = 0;
		try {
			const context = document.createElement('canvas').getContext('2d');
			if (context !== null) {
				context.font = font;
				const metrics = context.measureText(symbol);
				const boxCentre = (metrics.fontBoundingBoxDescent - metrics.fontBoundingBoxAscent) / 2;
				const inkCentre = (metrics.actualBoundingBoxDescent - metrics.actualBoundingBoxAscent) / 2;
				if (Number.isFinite(boxCentre) && Number.isFinite(inkCentre)) {
					shift = Math.round(boxCentre - inkCentre);
				}
			}
		} catch {
			shift = 0;
		}

		if (settled) nudges.set(key, shift);
		return shift;
	}

	/** Applies it, and again once webfonts have settled — metrics change when the face does. */
	export function opticalCentre(node: HTMLElement, symbol: string) {
		const apply = (current: string): void => {
			const style = getComputedStyle(node);
			const font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
			const shift = opticalNudge(current, font);
			node.style.transform = shift === 0 ? '' : `translateY(${shift}px)`;
		};

		apply(symbol);
		// A face that arrives late changes the metrics under a nudge already applied.
		void document.fonts?.ready?.then(() => apply(node.textContent ?? symbol));

		return {
			update(next: string) {
				apply(next);
			}
		};
	}
</script>

<script lang="ts">
	/**
	 * The one control for **choosing a currency**, everywhere this console offers the choice: a
	 * price's base currency, the currency an amount is being added in, and the analytics filter.
	 * (The storefront's selector is a copy of this in AbstractID, with the customer's pin around it.)
	 *
	 * It is a segmented control rather than a dropdown because a currency list is short, closed and
	 * already familiar — three or four options a customer recognises at a glance from `$`, `€`, `¥`.
	 * A dropdown hides all of them behind a click and says only what is currently chosen; the
	 * segments say what is on offer *and* what is chosen, in the space the closed dropdown occupied.
	 *
	 * **It is one pill divided into sections, not a row of separate pills.** The difference is not
	 * decoration: separate pills are a set of things each of which happens to be pressable, and the
	 * reader has to infer that pressing one un-presses the others. One track with a filled section
	 * moving along it says *these are the positions of a single control* before a word is read —
	 * which is exactly what a currency is, one choice with three answers rather than three switches.
	 * So the track is the trough (`bg-depth`, one hairline, rounded), the sections carry no border
	 * of their own, and the chosen one is filled.
	 *
	 * **The active section expands to name itself.** A symbol is a glyph, not a word: it is the
	 * fastest thing to recognise and the slowest thing to be sure of, so the one currency the store
	 * is actually pricing in states its full name — the figures on the page around it are only as
	 * unambiguous as this control is.
	 *
	 * Three rules keep it honest, and each is a way it could quietly lie:
	 *
	 * - **A symbol shared by two of the offered currencies stops being an identifier.** `$` is USD,
	 *   CAD, AUD and four others; `¥` is JPY and CNY; `kr` is three Nordic currencies. So when any
	 *   two of the currencies on offer carry the same symbol, **every** segment also shows its code
	 *   — uniformly, because a control where one segment reads `$ USD` and its neighbour reads `€`
	 *   invites the reader to think the two are different kinds of thing. Nothing is ever chosen
	 *   from an ambiguous glyph alone, and the accessible name always carries code and name in full.
	 * - **A segmented control does not scale**, so past `maxSegments` it renders `SelectField`
	 *   instead. The alternative is a strip that wraps to four lines in a filter bar or scrolls
	 *   sideways with the options that left the edge giving no sign they exist.
	 * - **The segments are real radios.** Keyboard traversal, the group's roving tab stop and the
	 *   focus ring all come from the input; a click handler on a `div` loses all three silently.
	 *
	 * A section that is **not** naming itself carries a hover popup that does, on hover and on
	 * focus. It is CSS rather than flowbite's `Popover` for a reason that is not preference:
	 * flowbite anchors a popover to its trigger by setting `tabIndex = 0` on it, and the trigger
	 * here is the `<label>` — so a four-currency picker would quietly add four tab stops in front
	 * of a control whose whole point is that it is *one* tab stop with arrow keys inside it. The
	 * active section has no popup, because the name it would show is already on screen beside the
	 * pointer.
	 */

	import { createEventDispatcher } from 'svelte';
	import SelectField from './SelectField.svelte';
	import type { StorefrontCurrency } from '../AppTypes';

	/** The rows on offer, in the order they should be read. */
	export let currencies: StorefrontCurrency[] = [];
	/** The chosen code. `''` is "nothing chosen", or the "any" segment where one is offered. */
	export let value: string = '';
	export let disabled: boolean = false;
	export let compact: boolean = false;
	/** Wired to the group; also the seed for the radios' shared `name`. */
	export let id: string = '';
	/**
	 * The group's accessible name. The visible eyebrow beside the control is not a `<label>` — a
	 * radio group has no single control for one to point at — so the name is stated here.
	 */
	export let label: string = 'Currency';
	/**
	 * When set, a leading segment meaning "no currency in particular", worth `''`. The filter bar
	 * needs it; a form choosing the currency an amount is denominated in must not have it.
	 */
	export let anyLabel: string | null = null;
	/** Past this many segments the control falls back to a dropdown. See the note above. */
	export let maxSegments: number = 6;

	let cssClass: string = '';
	export { cssClass as class };

	const dispatcher = createEventDispatcher<{ change: { value: string } }>();

	const groupName = `currency-picker-${(sequence += 1)}`;

	interface Segment {
		value: string;
		/** The glyph, or the short word standing in for "any". */
		symbol: string;
		/** What the active segment expands to, and what the accessible name is built from. */
		name: string;
		/** The ISO code, where there is one. */
		code: string;
	}

	$: segments = [
		...(anyLabel === null ? [] : [{ value: '', symbol: 'All', name: anyLabel, code: '' }]),
		...currencies.map(currency => ({
			value: currency.code,
			symbol: currency.symbol,
			name: currency.name,
			code: currency.code.toUpperCase()
		}))
	] as Segment[];

	/*
	 * Whether a glyph is enough to tell these apart. Compared case-insensitively because a symbol
	 * is free text an admin types: `Kr` and `kr` are the same glyph to a reader and would otherwise
	 * be counted as two.
	 */
	$: symbols = currencies.map(currency => currency.symbol.trim().toLowerCase());
	$: symbolsCollide = new Set(symbols).size < symbols.length;

	/** Whether this segment shows its code beside the glyph. */
	function fnShowsCode(segment: Segment): boolean {
		if (segment.code.length === 0) return false;
		// A symbol that *is* the code ("CHF") would otherwise render it twice.
		if (segment.symbol.trim().toUpperCase() === segment.code) return false;
		return symbolsCollide;
	}

	/**
	 * The name assistive technology reads, which never depends on the collision rule above: what a
	 * segment offers has to be unambiguous whether or not the glyph beside it happens to be.
	 */
	function fnAccessibleName(segment: Segment): string {
		return segment.code.length === 0 ? segment.name : `${segment.name} (${segment.code})`;
	}

	/*
	 * Whole literal class strings per branch. Tailwind's scanner reads source text, so a class
	 * assembled at runtime is invisible to it and the rule silently never ships.
	 */
	/*
	 * The track: one pill, one hairline, holding the sections. `flex-wrap` is the concession to a
	 * narrow screen — a track that wraps is odd-looking, and one that overflows takes the segments
	 * that left the edge with it, giving no sign they were ever there.
	 */
	const TRACK_BASE =
		'inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-full border border-widget-color bg-depth p-0.5';

	/*
	 * A section carries no border of its own: the pill around them is the border, and one per
	 * section would draw three controls where there is one. The chosen section is **filled** rather
	 * than outlined for the same reason — on a shared track, a fill is what reads as a position.
	 *
	 * `primary-600` rather than `primary-500`: white on 500 is about 3.7:1, which is under AA for
	 * body text, and this section is the one carrying the currency's name in full.
	 */
	const SEGMENT_BASE =
		'group/segment relative inline-flex min-w-0 items-center rounded-full transition-colors focus-within:ring-2 focus-within:ring-primary-500';

	/*
	 * The hover popup. `pointer-events-none` so it cannot come between the pointer and the section
	 * it describes, and flowbite's own tooltip palette rather than a depth surface — a floating
	 * overlay is the one thing on the page that is not sitting on a surface the depth ramp knows
	 * about, and the app's other popovers already look like this.
	 */
	const TOOLTIP_CLASS =
		'pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover/segment:opacity-100 group-focus-within/segment:opacity-100 motion-reduce:transition-none dark:bg-zinc-700';
	$: sizeClass = compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm';
	$: symbolClass = compact ? 'font-semibold text-sm' : 'font-semibold text-base';

	function fnSegmentClass(isActive: boolean, isDisabled: boolean): string {
		const state = isActive
			? 'bg-primary-600 text-white shadow-sm'
			: 'text-default';
		const interaction = isDisabled
			? 'cursor-not-allowed'
			: isActive
				? 'cursor-pointer'
				: 'cursor-pointer hover:text-primary-600 dark:hover:text-primary-400';
		return `${SEGMENT_BASE} ${sizeClass} ${state} ${interaction}`;
	}

	function fnChoose(next: string): void {
		if (disabled || next === value) return;
		value = next;
		dispatcher('change', { value: next });
	}

	/** The dropdown this falls back to, past `maxSegments`. */
	$: items = segments.map(segment => ({
		value: segment.value,
		name: segment.code.length === 0 ? segment.name : `${segment.symbol} ${segment.code} (${segment.name})`
	}));
	$: isSegmented = segments.length > 0 && segments.length <= maxSegments;
</script>

{#if segments.length === 0}
	<!-- Nothing to choose from is not an empty control, it is no control. -->
{:else if isSegmented}
	<div
		{id}
		role="radiogroup"
		aria-label={label}
		aria-disabled={disabled ? 'true' : undefined}
		class="{TRACK_BASE} {disabled ? 'opacity-60' : ''} {cssClass}"
	>
		{#each segments as segment (segment.value)}
			{@const isActive = segment.value === value}
			<!-- svelte-ignore a11y-label-has-associated-control -->
			<label class={fnSegmentClass(isActive, disabled)}>
				<input
					type="radio"
					class="sr-only"
					name={id.length > 0 ? `${id}-currency` : groupName}
					value={segment.value}
					checked={isActive}
					{disabled}
					aria-label={fnAccessibleName(segment)}
					on:change={() => fnChoose(segment.value)}
				/>
				<span class="inline-flex items-center gap-1.5" aria-hidden="true">
					<span class="{symbolClass} inline-block" use:opticalCentre={segment.symbol}>{segment.symbol}</span>
					{#if fnShowsCode(segment)}
						<span class="font-mono text-[11px] tracking-wide">{segment.code}</span>
					{/if}
				</span>
				<!--
					The expansion. A `0fr → 1fr` grid column rather than a width, because the name's
					width is not known here and a `max-width` guessed large enough to fit the longest
					currency name animates at a speed that has nothing to do with the distance it
					actually travels.
				-->
				<span
					class="grid transition-[grid-template-columns] duration-200 ease-out motion-reduce:transition-none"
					style={`grid-template-columns: ${isActive ? '1fr' : '0fr'}`}
					aria-hidden="true"
				>
					<span class="overflow-hidden whitespace-nowrap"><span class="pl-1.5">{segment.name}</span></span>
				</span>
				{#if !isActive}
					<span class={TOOLTIP_CLASS} aria-hidden="true">{fnAccessibleName(segment)}</span>
				{/if}
			</label>
		{/each}
	</div>
{:else}
	<!-- Past `maxSegments` the strip stops being readable at a glance, which was the whole point. -->
	<SelectField
		{id}
		{items}
		{value}
		{disabled}
		size={compact ? 'sm' : 'md'}
		class={cssClass}
		placeholder={label}
		on:change={event => fnChoose(event.detail.value)}
	/>
{/if}
